import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';

interface Stats {
  totalDevices: number;
  totalVenues: number;
  activeSessions: number;
}

const AdminDashboard = () => {
  const [stats, setStats] = useState<Stats>({
    totalDevices: 0,
    totalVenues: 0,
    activeSessions: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const [venuesRes] = await Promise.all([
        api.get('/venues'),
      ]);

      const venues = venuesRes.data;
      const totalVenues = venues.length;
      const totalDevices = venues.reduce((sum: number, venue: any) =>
        sum + (venue.devices?.length || 0), 0
      );

      setStats({
        totalDevices,
        totalVenues,
        activeSessions: 0,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-black mb-4 bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
            🛠️ Administrace
          </h1>
          <p className="text-gray-600 text-lg">
            Spravujte zařízení i provozovny a sledujte aktivitu systému
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-card hover:shadow-glow transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="p-4 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl">
                <span className="text-4xl">🎮</span>
              </div>
              <div className="text-right">
                <p className="text-gray-600 text-sm">Počet zařízení</p>
                <p className="text-4xl font-bold text-primary-600">
                  {loading ? '...' : stats.totalDevices}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-card hover:shadow-glow transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="p-4 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-2xl">
                <span className="text-4xl">🏢</span>
              </div>
              <div className="text-right">
                <p className="text-gray-600 text-sm">Počet provozoven</p>
                <p className="text-4xl font-bold text-secondary-600">
                  {loading ? '...' : stats.totalVenues}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-card hover:shadow-glow transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="p-4 bg-gradient-to-br from-accent-500 to-accent-600 rounded-2xl">
                <span className="text-4xl">⚡</span>
              </div>
              <div className="text-right">
                <p className="text-gray-600 text-sm">Aktivní sezení</p>
                <p className="text-4xl font-bold text-accent-600">
                  {loading ? '...' : stats.activeSessions}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Link
            to="/admin/devices"
            className="group bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-card hover:shadow-glow-lg transition-all duration-300 hover:scale-105 transform"
          >
            <div className="flex items-center space-x-4 mb-4">
              <div className="p-4 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl group-hover:scale-110 transition-transform duration-300">
                <span className="text-5xl">🎮</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-1">
                  Správa zařízení
                </h2>
                <p className="text-gray-600">
                  Vytvářejte zařízení, generujte API klíče a sledujte stav
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end text-primary-600 font-semibold group-hover:translate-x-2 transition-transform duration-300">
              Spravovat zařízení
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </Link>

          <Link
            to="/admin/venues"
            className="group bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-card hover:shadow-glow-lg transition-all duration-300 hover:scale-105 transform"
          >
            <div className="flex items-center space-x-4 mb-4">
              <div className="p-4 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-2xl group-hover:scale-110 transition-transform duration-300">
                <span className="text-5xl">🏢</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-1">
                  Správa provozoven
                </h2>
                <p className="text-gray-600">
                  Vytvářejte, upravujte a spravujte provozovny
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end text-secondary-600 font-semibold group-hover:translate-x-2 transition-transform duration-300">
              Spravovat provozovny
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
